import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { convertPredicate, createPredicate, fetchPredicateMetrics, updatePredicate } from './api';
import {
  ConvertPredicateRequest,
  ConvertPredicateResponse,
  CreatePredicateRequest,
  UpdatePredicateRequest,
} from './types';

export const predicateKeys = {
  all: [API_TAGS.PREDICATES] as const,
  details: () => [...predicateKeys.all, 'detail'] as const,
  detail: (id: number) => [...predicateKeys.details(), id] as const,
  metrics: () => [...predicateKeys.all, 'metrics'] as const,
  convert: (payload?: Partial<ConvertPredicateRequest>) => [...predicateKeys.all, 'convert', payload ?? {}] as const,
};

export const usePredicateMetrics = () => {
  return useQuery({
    queryKey: predicateKeys.metrics(),
    queryFn: () => fetchPredicateMetrics(),
  });
};

export const useConvertPredicate = () => {
  return useMutation<ConvertPredicateResponse, unknown, ConvertPredicateRequest>({
    mutationFn: (payload) => convertPredicate(payload),
  });
};

export const useCreatePredicate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePredicateRequest) => createPredicate(payload),
    onSuccess: () => {
      // invalidate potential lists once they appear in the app
      queryClient.invalidateQueries({ queryKey: predicateKeys.all });
    },
  });
};

export const useUpdatePredicate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { predicateId: number; body: UpdatePredicateRequest }) => updatePredicate(params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: predicateKeys.detail(variables.predicateId) });
      queryClient.invalidateQueries({ queryKey: predicateKeys.all });
    },
  });
};
